export const SONG_BASED_PHASE_LABELS = {
  transcribe: 'Transcribe song',
  split: 'AI lyric segmentation',
  theme: 'Theme detection',
  align: 'WhisperX alignment',
  beat: 'Beat-sync motion',
  screenplay: 'Screenplay & prompts',
  prompt: 'Image prompt',
  image: 'Generate image',
};

export function segmentHasWhisperxAlign(seg) {
  if (seg?.alignment_source === 'whisperx') {
    if (seg.align_status === 'completed') return true;
    if (seg.statement_start_time != null && seg.statement_end_time != null) return true;
    if (Array.isArray(seg?.word_timestamps) && seg.word_timestamps.length > 0) return true;
  }
  if (seg?.align_status === 'completed') {
    if (Array.isArray(seg?.word_timestamps) && seg.word_timestamps.length > 0) return true;
    if (seg.statement_start_time != null && seg.statement_end_time != null) return true;
  }
  return false;
}

/** WhisperX or audio-duration fallback — enough to continue the song-based pipeline. */
export function segmentHasAlignTiming(seg) {
  if (segmentHasWhisperxAlign(seg)) return true;
  if (seg?.alignment_source === 'audio_duration' && seg?.align_status === 'completed') {
    return seg.statement_start_time != null || seg.statement_start_time_local != null;
  }
  return false;
}

export function segmentHasImagePrompt(seg) {
  if (!segmentHasAlignTiming(seg)) return false;
  const prompt = (seg?.image_prompt || '').trim();
  if (prompt) return true;
  const visual = (seg?.visual || '').trim();
  const text = (seg?.text || seg?.authored_script || '').trim();
  return visual.length > 0 && visual !== text;
}

export function segmentHasImage(seg) {
  return Boolean((seg?.image_url || seg?.source_image_url || '').trim());
}

/** Ordered pipeline steps with done/current flags for the popup UI. */
export function buildSongBasedNextSteps(segments) {
  const audioSegs = (segments || []).filter((s) => s.audio_url);
  const total = audioSegs.length;
  const aligned = audioSegs.filter((s) => segmentHasAlignTiming(s)).length;
  const prompted = audioSegs.filter((s) => segmentHasImagePrompt(s)).length;
  const imaged = audioSegs.filter((s) => segmentHasImage(s)).length;
  const generating = audioSegs.filter((s) => String(s.image_status || '').toLowerCase() === 'running').length;

  const alignDone = total > 0 && aligned >= total;
  const promptsDone = alignDone && prompted >= total;
  const imagesDone = promptsDone && imaged >= total;

  let current = 'align';
  if (imagesDone) current = 'open';
  else if (promptsDone) current = 'images';
  else if (alignDone) current = 'prompts';

  return [
    {
      id: 'align',
      label: 'WhisperX timing',
      detail: total ? `${aligned}/${total} stanzas` : 'Waiting for audio',
      done: alignDone,
      current: current === 'align',
    },
    {
      id: 'prompts',
      label: 'Screenplay & image prompts',
      detail: alignDone ? `${prompted}/${total} stanzas` : 'After WhisperX',
      done: promptsDone,
      current: current === 'prompts',
    },
    {
      id: 'images',
      label: 'Generate images',
      detail: promptsDone
        ? `${imaged}/${total} stanzas${generating ? ` · ${generating} queued` : ''}`
        : 'After prompts',
      done: imagesDone,
      current: current === 'images',
    },
    {
      id: 'open',
      label: 'Open stanzas',
      detail: 'Edit in stanza grid',
      done: imagesDone,
      current: current === 'open',
    },
  ];
}

export function songBasedReadyForStanzas(segments) {
  const steps = buildSongBasedNextSteps(segments);
  return steps.find((s) => s.id === 'open')?.done === true;
}

export function songBasedImagesInProgress(segments, job) {
  if (songBasedImagesJobStuck(job, segments) || songBasedImageStallError(job)) {
    return false;
  }
  const audioSegs = (segments || []).filter((s) => s.audio_url);
  const imaged = audioSegs.filter((s) => segmentHasImage(s)).length;
  const running = audioSegs.filter((s) => String(s.image_status || '').toLowerCase() === 'running').length;
  const pending = audioSegs.filter((s) => {
    if (segmentHasImage(s)) return false;
    const st = String(s.image_status || '').toLowerCase();
    return st === 'pending' || st === 'running' || !st;
  }).length;
  const jobRunning = job && !['completed', 'failed', 'cancelled'].includes(job.status);
  const jobMsg = String(job?.message || '').toLowerCase();
  const jobOnImages = jobMsg.includes('generating image') || jobMsg.includes('resuming image');
  return (jobRunning && jobOnImages) || running > 0 || (pending > 0 && imaged > 0 && imaged < audioSegs.length);
}

export function songBasedMissingImageSegments(segments) {
  return (segments || []).filter(
    (s) => s.audio_url && segmentHasAlignTiming(s) && !segmentHasImage(s),
  );
}

export function songBasedMissingAlignSegments(segments) {
  return (segments || []).filter((s) => s.audio_url && !segmentHasAlignTiming(s));
}

export function songBasedMissingAudioSegments(segments) {
  return (segments || []).filter(
    (s) => !(s?.audio_url || '').trim() && ((s?.text || s?.authored_script || '').trim()),
  );
}

function songBasedStuckTimeoutSec(job, step) {
  const songKey = {
    align: 'song_based_align_stuck_timeout_sec',
    tts: 'song_based_tts_stuck_timeout_sec',
    image: 'song_based_image_stuck_timeout_sec',
    prompt: 'song_based_prompt_stuck_timeout_sec',
  }[step];
  const scriptKey = {
    align: 'script_based_align_stuck_timeout_sec',
    tts: 'script_based_tts_stuck_timeout_sec',
    image: 'script_based_image_stuck_timeout_sec',
    prompt: 'script_based_prompt_stuck_timeout_sec',
  }[step];
  return Number(job?.[songKey])
    || Number(job?.song_based_stuck_timeout_sec)
    || Number(job?.[scriptKey])
    || Number(job?.script_based_stuck_timeout_sec)
    || 300;
}

function jobRunningPhaseIdlePastTimeout(job, timeoutSec, messageMatches) {
  if (!job || job.status !== 'running') return false;
  if (!messageMatches(String(job.message || ''))) return false;
  const updatedAt = job.updated_at ? Date.parse(job.updated_at) : NaN;
  if (!Number.isFinite(updatedAt)) return true;
  return (Date.now() - updatedAt) >= timeoutSec * 1000;
}

function imagePhaseMessage(msg) {
  const lower = String(msg || '').toLowerCase();
  return (
    lower.includes('generating image')
    || lower.includes('resuming image')
    || (lower.includes('image ready') && lower.includes('scene'))
  );
}

export function songBasedImageStallError(job) {
  return /image generation stalled/i.test(String(job?.error_message || ''));
}

function alignPhaseMessage(msg) {
  const lower = msg.toLowerCase();
  return lower.includes('whisperx');
}

function ttsPhaseMessage(msg) {
  const lower = msg.toLowerCase();
  return lower.includes('generating audio') || lower.includes('retrying audio') || lower.includes('resuming audio');
}

function songBasedResumeInFlight(job, phase) {
  const key = phase === 'image'
    ? 'song_image_resume_in_flight_at'
    : 'song_align_resume_in_flight_at';
  const started = job?.[key];
  if (!started) return false;
  const startedAt = Date.parse(started);
  if (!Number.isFinite(startedAt)) return true;
  const staleSec = Math.max(600, songBasedStuckTimeoutSec(job, phase) * 2);
  return (Date.now() - startedAt) < staleSec * 1000;
}

export function songBasedImagesJobStuck(job, segments) {
  const missing = songBasedMissingImageSegments(segments);
  if (!job) return false;
  if (songBasedImageStallError(job)) return true;
  if (!missing.length) return false;
  if (job.status === 'failed') return true;
  if (job.status !== 'running') return false;
  if (!imagePhaseMessage(job.message)) return false;
  return jobRunningPhaseIdlePastTimeout(
    job,
    songBasedStuckTimeoutSec(job, 'image'),
    () => true,
  );
}

export function songBasedAlignJobStuck(job, segments) {
  const missing = songBasedMissingAlignSegments(segments);
  if (!missing.length || !job) return false;
  return jobRunningPhaseIdlePastTimeout(
    job,
    songBasedStuckTimeoutSec(job, 'align'),
    alignPhaseMessage,
  );
}

export function songBasedTtsJobStuck(job, segments) {
  const missing = songBasedMissingAudioSegments(segments);
  if (!missing.length || !job) return false;
  return jobRunningPhaseIdlePastTimeout(
    job,
    songBasedStuckTimeoutSec(job, 'tts'),
    ttsPhaseMessage,
  );
}

/** Show Resume image generation after timeout, failure, or stall error on the job. */
export function songBasedShouldShowImageResume(job, segments) {
  if (!job) return false;
  if (job.status === 'cancelled' || job.status === 'completed') return false;
  if (songBasedResumeInFlight(job, 'image')) return false;
  const missing = songBasedMissingImageSegments(segments);
  if (songBasedImageStallError(job)) {
    return missing.length > 0 || job.status === 'failed' || job.status === 'running';
  }
  if (!missing.length) return false;
  if (job.status === 'failed') return true;
  if (job.status === 'running') return songBasedImagesJobStuck(job, segments);
  return false;
}

/** Show Resume WhisperX after timeout, failure, or partial song align stall. */
export function songBasedShouldShowAlignResume(job, segments) {
  const missing = songBasedMissingAlignSegments(segments);
  if (!missing.length || !job) return false;
  if (job.status === 'cancelled') return false;
  const stuck = songBasedAlignJobStuck(job, segments);
  if (songBasedResumeInFlight(job, 'align') && !stuck) return false;
  if (job.status === 'failed') return true;
  if (job.status === 'running') {
    if (stuck) return true;
    const msg = String(job.message || '').toLowerCase();
    if (msg.includes('re-running whisperx') || /whisperx alignment \d+\/\d+/i.test(msg)) {
      return jobRunningPhaseIdlePastTimeout(job, 120, alignPhaseMessage);
    }
  }
  return false;
}

/** Show resume/retry for audio (initial pipeline TTS) after timeout or failure. */
export function songBasedShouldShowAudioResume(job, segments) {
  const missing = songBasedMissingAudioSegments(segments);
  if (!missing.length || !job) return false;
  if (job.status === 'cancelled') return false;
  if (job.status === 'failed') return true;
  return songBasedTtsJobStuck(job, segments);
}

/** @deprecated Use songBasedShouldShowImageResume — kept for callers expecting the old name. */
export function songBasedCanResumeImages(segments, job) {
  return songBasedShouldShowImageResume(job, segments);
}

/** Stanzas for reel/list once WhisperX timing is done. */
export function songBasedShowStanzaViews(segments) {
  const audioSegs = (segments || []).filter((s) => s.audio_url);
  if (!audioSegs.length) return false;
  return audioSegs.every((s) => segmentHasAlignTiming(s));
}
